import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, {type SelectChangeEvent} from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha, type Theme, useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {HeadlineValue} from "../../kit/Headline";
import {InsightsCard} from "../../kit/InsightsCard";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {SeriesCard} from "../../kit/SeriesCard";
import type {InsightsLoading} from "../../kit/insightsLoading";
import {useBarGrowth} from "../../../surveys/insights/base/useBarGrowth";
import {BAR_DRAW_DURATION, BAR_RADIUS} from "../../../surveys/insights/constants";
import type {MarketingLifecycleSegmentV4} from "../../models/marketing-v4";

/** How far a guest has come with the house. The order is the journey, so it is fixed here. */
const LIFECYCLE_STAGES = [
    {key: "prospects", label: "Prospects", description: "No stays yet"},
    {key: "new", label: "New", description: "First stay, within 12 months"},
    {key: "regulars", label: "Regulars", description: "2+ stays, active within 12 months"},
    {key: "atRisk", label: "At-risk", description: "Was active, quiet for 12-24 months"},
    {key: "dormant", label: "Dormant", description: "No stay in 2+ years"},
] as const;

const STAGE_BY_KEY = new Map(LIFECYCLE_STAGES.map(stage => [stage.key as string, stage]));

const BASE_BAR_HEIGHT = 14;

/**
 * Warm to cold along the journey: a prospect is nobody yet, a regular is the win, dormant has gone
 * quiet. Colour carries the meaning, so nothing passes one in.
 */
function stageColor(theme: Theme, key: string): string {
    switch (key) {
        case "new":
            return theme.palette.info.main;
        case "regulars":
            return theme.palette.success.main;
        case "atRisk":
            return theme.palette.warning.main;
        case "dormant":
            return alpha(theme.palette.text.primary, 0.38);
        default:
            return alpha(theme.palette.text.primary, 0.22);
    }
}

interface StageRow {
    key: string;
    label: string;
    description: string;
    count: number;
    share: number;
    color: string;
}

interface Props {
    segments?: MarketingLifecycleSegmentV4[];
    loading?: InsightsLoading;
}

/**
 * The whole guest base as one bar, split by lifecycle stage. Everything else on this page targets a
 * slice of it, so it reads as one figure with its composition underneath rather than as five tiles.
 */
export function MarketingGuestBaseCard({segments: segmentData, loading}: Props) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const selectLabelId = React.useId();
    const [excludedKeys, setExcludedKeys] = React.useState<string[]>([]);

    const locale = i18n.resolvedLanguage || i18n.language;
    const count = new Intl.NumberFormat(locale);
    const percent = new Intl.NumberFormat(locale, {maximumFractionDigits: 1});

    // Every known stage shows even at zero — a stage nobody is in is itself the finding — and any
    // stage the API adds later is appended rather than dropped.
    const known = LIFECYCLE_STAGES.map(stage => {
        const match = (segmentData ?? []).find(segment => segment.key === stage.key);

        return {key: stage.key as string, label: match?.label ?? stage.label, count: match?.count ?? 0};
    });
    const extra = (segmentData ?? []).filter(segment => !STAGE_BY_KEY.has(segment.key));
    const segments = [...known, ...extra];

    const selected = segments.filter(segment => !excludedKeys.includes(segment.key));
    const selectedKeys = selected.map(segment => segment.key);
    const total = selected.reduce((sum, segment) => sum + segment.count, 0);
    const grown = useBarGrowth(`${selectedKeys.join(",")}:${total}`);

    const rows: StageRow[] = selected.map(segment => ({
        key: segment.key,
        label: t(segment.label),
        description: t(STAGE_BY_KEY.get(segment.key)?.description ?? ""),
        count: segment.count,
        share: total === 0 ? 0 : (segment.count / total) * 100,
        color: stageColor(theme, segment.key),
    }));

    const hasData = total > 0;

    const renderSelection = (keys: string[]) => {
        if (keys.length === segments.length) return t("All lifecycle stages");
        if (keys.length === 0) return t("No lifecycle stages selected");
        if (keys.length === 1) return t(segments.find(segment => segment.key === keys[0])?.label ?? keys[0]);
        return t("{{count}} lifecycle stages", {count: keys.length});
    };

    const onSelectionChange = (event: SelectChangeEvent<string[]>) => {
        const next = typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;

        setExcludedKeys(segments.filter(segment => !next.includes(segment.key)).map(segment => segment.key));
    };

    const columns: InsightsColumn<StageRow>[] = [
        {key: "stage", label: t("Lifecycle stages"), kind: "label", cell: row => row.label},
        {key: "count", label: t("Guests"), kind: "value", cell: row => count.format(row.count)},
        {key: "share", label: t("Share"), kind: "value", cell: row => `${percent.format(row.share)}%`},
    ];

    return (
        <SeriesCard
            title={t("Guest base")}
            hint={t("Every guest on file, split by how far they have come with the house. Pick stages to narrow the figure.")}
            loading={loading}
            // One bar, not a plot: the card is as tall as what it draws.
            chartMinHeight={BASE_BAR_HEIGHT + 16}
            headline={<HeadlineValue value={count.format(total)} suffix={t("guests")}/>}
            actions={(
                <FormControl size={"small"} sx={{minWidth: 200}}>
                    <InputLabel id={selectLabelId}>{t("Lifecycle stages")}</InputLabel>
                    <Select<string[]>
                        multiple
                        labelId={selectLabelId}
                        label={t("Lifecycle stages")}
                        value={selectedKeys}
                        onChange={onSelectionChange}
                        renderValue={renderSelection}
                        sx={{height: 36, "& .MuiSelect-select": {py: 0.75}}}
                        MenuProps={{PaperProps: {sx: {maxHeight: 360}}}}
                    >
                        {segments.map(segment => (
                            <MenuItem key={segment.key} value={segment.key}>
                                <Checkbox checked={selectedKeys.includes(segment.key)}/>
                                <ListItemText
                                    primary={t(segment.label)}
                                    secondary={t(STAGE_BY_KEY.get(segment.key)?.description ?? "")}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            empty={hasData ? undefined : (
                <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"} sx={{py: 4}}>
                    {t("No guests in the selected lifecycle stages")}
                </Typography>
            )}
            legend={(
                <InsightsLegend>
                    {rows.map(row => (
                        <LegendEntry key={row.key} shape={"bar"} color={row.color} label={`${row.label} ${count.format(row.count)}`}/>
                    ))}
                </InsightsLegend>
            )}
            table={<InsightsTable label={t("Guest base")} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <Stack direction={"row"} sx={{width: "100%", minWidth: 0, gap: "3px", py: 1}}>
                    {rows.map(row => (
                        <InsightsTooltip
                            key={row.key}
                            placement={"top"}
                            title={(
                                <React.Fragment>
                                    <TooltipTitle title={row.label} subtitle={row.description || undefined}/>
                                    <TooltipRow swatch={row.color} label={t("Guests")} value={count.format(row.count)}/>
                                    <TooltipRow label={t("Share")} value={`${percent.format(row.share)}%`}/>
                                </React.Fragment>
                            )}
                        >
                            <Box
                                sx={{
                                    height: BASE_BAR_HEIGHT,
                                    // A stage nobody is in keeps no width; one guest still keeps a sliver.
                                    flexGrow: row.count,
                                    flexBasis: 0,
                                    minWidth: row.count > 0 ? 3 : 0,
                                    borderRadius: `${BAR_RADIUS}px`,
                                    bgcolor: row.color,
                                    cursor: "default",
                                    transform: grown ? "scaleX(1)" : "scaleX(0)",
                                    transformOrigin: "left",
                                    transition: `transform ${BAR_DRAW_DURATION}ms ease-out`,
                                }}
                            />
                        </InsightsTooltip>
                    ))}
                </Stack>
            )}
        />
    );
}

export default MarketingGuestBaseCard;
